
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IoCloudUploadOutline, IoAddOutline, IoChevronDownOutline, IoCloseOutline } from 'react-icons/io5';
import { AiOutlineCheckCircle } from 'react-icons/ai';
import { FaArrowLeft } from 'react-icons/fa';
import {
    getAllEditingPlans,
    addToCart,
    startUpload,
    getPartUrl,
    uploadChunkToPresignedUrl,
    completeUpload,
    updateBookingMedia,
    getMyCart,
    deleteMedia
} from '../../utils/APIs/editingApis';
import { getAllGalleryImages } from '../../utils/APIs/galleryApis';
import { getOneUpcomingBookingById } from '../../utils/APIs/bookingApis';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLocation } from 'react-router-dom';
import './UploadMediaPage.css';

const isUploadFailed = (file) =>
    Boolean(file?.error) || /failed/i.test(String(file?.status || ""));

const getFileStatusClass = (file) => {
    if (file?.uploaded || /uploaded successfully/i.test(String(file?.status || ""))) {
        return "success";
    }
    if (isUploadFailed(file)) {
        return "error";
    }
    return "pending";
};

const UploadMediaPage = () => {
    const [plan, setPlan] = useState('standard');
    const [allPlans, setAllPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [standardEditingVideos, setStandardEditingVideos] = useState(0);
    const [premiumEditingVideos, setPremiumEditingVideos] = useState(0);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const fileInputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [bookingDetails, setBookingDetails] = useState(null);
    const [searchParams] = useSearchParams();
    const bookingId = searchParams.get('bookingId');
    const cartId = searchParams.get('cartId');
    const navigate = useNavigate();
    const bookingState = useLocation();
    console.log("booking state",bookingState);

    const isQuoteEditingBooking = (bookingDetails?.serviceCategory === 'editing' || bookingDetails?.serviceCategory === 'Editing') && 
        (!bookingDetails?.editingbookings || bookingDetails?.editingbookings.length === 0 || bookingDetails?.isQuote || !!bookingDetails?.quoteId);


    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

    const fetchBookingDetails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getOneUpcomingBookingById(bookingId);
            if (response?.data?.success) {
                const booking = response.data.data;
                setBookingDetails(booking);
                const stdCount = booking.standardEditingVideos !== undefined ? booking.standardEditingVideos : (booking.standardEditingVideos || 0);
                const premCount = booking.premiumEditingVideos !== undefined ? booking.premiumEditingVideos : (booking.premiumEditingVideos || 0);
                
                setStandardEditingVideos(stdCount);
                setPremiumEditingVideos(premCount);

                // Auto-select tab based on available counts
                if (premCount > 0 && stdCount === 0) {
                    setPlan('premium');
                } else {
                    setPlan('standard');
                }

                // Load existing media from DataLinks (Gallery API)
                const galleryRes = await getAllGalleryImages(1, 100, bookingId);
                if (galleryRes?.data?.success) {
                    const mediaArray = galleryRes.data.data || [];
                    const baseUrl = process.env.REACT_APP_API_URL || '';
                    
                    const existingFiles = mediaArray.filter(m => !m.photographerId).map(m => {
                        const url = m.imageUrl || m.url || m.key || '';
                        const key = m.key;
                        const name = key.split('/').pop() || 'Existing File';
                        
                        // Construct streaming URL if needed
                        const cleanUrl = key.startsWith('/') ? key.substring(1) : key;
                        const fullUrl = (url && url.startsWith('http')) ? url : `${baseUrl}/api/users/stream/${bookingId}/${cleanUrl}`;

                        return {
                            id: m._id || Math.random(),
                            name: name,
                            status: 'Previously Uploaded',
                            uploaded: true,
                            progress: 100,
                            thumbnail: fullUrl,
                            fileKey: key,
                            type: name.match(/\.(mp4|webm|ogg|mov|mkv)$/i) ? 'video/mp4' : 'image/jpeg',
                            category: m.category || 'standard'
                        };
                    });
                    setFiles(existingFiles);
                }
            }
        } catch (error) {
            console.error("Error fetching booking details:", error);
            toast.error("Failed to load booking details");
        } finally {
            setLoading(false);
        }
    }, [bookingId]);

    const fetchCartDetails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getMyCart();
            if (response?.data?.success) {
                const cart = response.data.data;
                const items = cart.items || [];
                const cartIdFromParams = searchParams.get('cartId');
                const cartItem = items.find(item => item._id === cartIdFromParams || item.id === cartIdFromParams);
                
                if (cartItem && cartItem.fileKeys?.length > 0) {
                    const existingFiles = cartItem.fileKeys.map(key => ({
                        id: Math.random(),
                        name: key.split('/').pop() || 'Existing File',
                        status: 'In Cart',
                        uploaded: true,
                        progress: 100,
                        thumbnail: key,
                        fileKey: key,
                        type: key.match(/\.(mp4|webm|ogg|mov|mkv)$/i) ? 'video/mp4' : 'image/jpeg'
                    }));
                    setFiles(existingFiles);
                }
            }
        } catch (error) {
            console.error("Error fetching cart details:", error);
        } finally {
            setLoading(false);
        }
    }, [searchParams]);

    const fetchPlans = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getAllEditingPlans();
            if (response?.data?.success) {
                const plans = response.data.editingPlans || response.data.plans || response.data.plansDetails || [];
                setAllPlans(plans);
            }
        } catch (error) {
            console.error("Error fetching editing plans:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchPlans();

        if (bookingId) {
            fetchBookingDetails();
        } else if (cartId) {
            fetchCartDetails();
        }
    }, [bookingId, cartId, fetchBookingDetails, fetchCartDetails, fetchPlans]);

    // Separate useEffect for cleaning up object URLs
    useEffect(() => {
        return () => {
            files.forEach(file => {
                if (file.thumbnail && file.thumbnail.startsWith('blob:')) {
                    URL.revokeObjectURL(file.thumbnail);
                }
            });
        };
    }, [files]);

    useEffect(() => {
        // Sync for Booking
        if (allPlans.length > 0 && bookingDetails && (bookingDetails.serviceCategory === 'editing' || bookingDetails.service_id?.serviceName?.toLowerCase().includes('editing'))) {
            const bookedPlanName = bookingDetails.editingbookings?.[0]?.planName;
            if (bookedPlanName) {
                const matchedPlan = allPlans.find(p => (p.planName || "").toLowerCase() === bookedPlanName.toLowerCase());
                if (matchedPlan) {
                    setSelectedPlan(matchedPlan);
                    setPlan(matchedPlan.category || 'standard');
                }
            }
        }

        // Sync for Cart
        if (allPlans.length > 0 && cartId && !bookingId) {
            const fetchAndMatchCart = async () => {
                const response = await getMyCart();
                if (response?.data?.success) {
                    const items = response.data.data.items || [];
                    const cartItem = items.find(item => item._id === cartId || item.id === cartId);
                    if (cartItem) {
                        const matchedPlan = allPlans.find(p => p._id === cartItem.editingPlanId || p.planName === cartItem.name);
                        if (matchedPlan) {
                            setSelectedPlan(matchedPlan);
                            setPlan(matchedPlan.category || 'standard');
                        }
                    }
                }
            };
            fetchAndMatchCart();
        }
    }, [allPlans, bookingDetails, cartId, bookingId]);

    const handlePlanChange = (newPlan) => {
        if (cartId) return; // Prevent switching if linked to specific cart item
        setPlan(newPlan);
        setSelectedPlan(null);
        setShowDropdown(false);
    };

    const handleQuantitySelect = (p) => {
        setSelectedPlan(p);
        setShowDropdown(false);
    };

    const handleFileUpload = async (event) => {
        const uploadedFiles = Array.from(event.target.files);
        if (uploadedFiles.length === 0) return;

        let filesToProcess = uploadedFiles;
        
        // Filter out non-video files (allow JPEG images for custom quotes)
        const allowedFiles = uploadedFiles.filter(file => {
            if (file.type.startsWith('video/')) return true;
            if (isQuoteEditingBooking && (file.type === 'image/jpeg' || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg'))) return true;
            return false;
        });

        if (allowedFiles.length < uploadedFiles.length) {
            toast.error(isQuoteEditingBooking ? "Only video and JPEG image files are allowed for upload." : "Only video files are allowed for upload.");
            filesToProcess = allowedFiles;
        }

        if (filesToProcess.length === 0) return;

        if (!isQuoteEditingBooking) {
            let maxFiles = selectedPlan?.numberOfVideos || 0;
            
            if (bookingId) {
                maxFiles = plan === 'premium' ? premiumEditingVideos : standardEditingVideos;
            }

            if (!maxFiles && !bookingId && !selectedPlan) {
                toast.error("Please select a plan first to know your upload limit.");
                return;
            }

            const currentFilesCount = bookingId ? files.filter(f => f.category === plan).length : files.length;
            const remainingSlots = maxFiles - currentFilesCount;

            if (remainingSlots <= 0) {
                toast.warning(`You have already reached the limit of ${maxFiles} files for this category.`);
                return;
            }

            if (filesToProcess.length > remainingSlots) {
                toast.info(`Plan limit reached. Only the first ${remainingSlots} files will be uploaded.`);
                filesToProcess = filesToProcess.slice(0, remainingSlots);
            }
        }

        const totalFiles = filesToProcess.length;
        let completedFiles = 0;

        // Global progress is no longer tracked separately since we use currentFileProgress
        


        for (const file of filesToProcess) {

            const fileId = Date.now() + Math.random();
            const newFile = {
                id: fileId,
                name: file.name,
                status: 'Initializing...',
                uploaded: false,
                progress: 0,
                thumbnail: URL.createObjectURL(file),
                type: file.type,
                category: plan
            };
            
            setFiles(prev => [...prev, newFile]);



            try {

                // 1. Start Upload
                const uploadRef = bookingDetails?.veroaBookingId || bookingId || `cart-upload-${Date.now()}`;
                const startRes = await startUpload(file.name, file.type, uploadRef, file.size);
                // Check if we have uploadId directly or inside success wrapper
                const uploadData = startRes.data?.success ? startRes.data.data : startRes.data;

                if (!uploadData?.key) {
                    throw new Error(startRes?.data?.message || "Failed to start upload");
                }

                const { key } = uploadData;

                // Single PUT upload (< 100MB) — presigned PutObject URL
                if (uploadData.strategy === "single" || uploadData.uploadUrl) {
                    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: "Uploading...", progress: 0 } : f));

                    await uploadChunkToPresignedUrl(
                        uploadData.uploadUrl,
                        file,
                        file.type,
                        (progress) => {
                            setFiles(prev => prev.map(f => f.id === fileId ? { ...f, progress } : f));
                        }
                    );

                    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: "Finalizing...", progress: 99 } : f));
                    const completeRes = await completeUpload(null, key, [], bookingId, bookingDetails?.veroaBookingId, plan);
                    const completeData = completeRes.data?.success !== undefined ? completeRes.data : { success: true, ...completeRes.data };

                    if (!completeData.success && completeData.error) {
                        throw new Error(completeData.error || completeData.message || "Failed to complete upload");
                    }

                    setFiles(prev => prev.map(f => f.id === fileId ? {
                        ...f,
                        status: "Uploaded successfully",
                        uploaded: true,
                        progress: 100,
                        fileKey: completeData.key || completeData.data?.key || key
                    } : f));
                    toast.success(`${file.name} uploaded!`);
                    completedFiles++;
                    continue;
                }

                if (!uploadData?.uploadId) {
                    throw new Error(startRes?.data?.message || "Failed to start multipart upload");
                }

                const { uploadId, chunkSize } = uploadData;
                const currentChunkSize = chunkSize || CHUNK_SIZE;
                const totalParts = Math.ceil(file.size / currentChunkSize);

                // 2. Upload Chunks directly to Spaces (presigned URLs)
                for (let i = 1; i <= totalParts; i++) {
                    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: `Uploading part ${i}/${totalParts}...` } : f));

                    const start = (i - 1) * currentChunkSize;
                    const end = Math.min(i * currentChunkSize, file.size);
                    const chunk = file.slice(start, end);

                    const urlRes = await getPartUrl(uploadId, key, i);
                    const uploadUrl = urlRes.data?.uploadUrl || urlRes.data?.data?.uploadUrl;

                    if (!uploadUrl) {
                        throw new Error(urlRes?.data?.message || "Failed to get upload URL for chunk");
                    }

                    const uploadRes = await uploadChunkToPresignedUrl(
                        uploadUrl,
                        chunk,
                        file.type,
                        (progress) => {
                        const chunkWeight = 100 / totalParts;
                        const fileProgress = Math.round(((i - 1) * chunkWeight) + (progress * chunkWeight / 100));
                        setFiles(prev => prev.map(f => f.id === fileId ? { ...f, progress: fileProgress } : f));
                        },
                        { isMultipartPart: true }
                    );

                    if (uploadRes.status < 200 || uploadRes.status >= 300) {
                        throw new Error("Chunk upload failed");
                    }
                }

                // 3. Complete Upload — server lists parts from Spaces (small JSON body)
                setFiles(prev => prev.map(f => f.id === fileId ? { ...f, status: 'Finalizing...', progress: 99 } : f));
                const completeRes = await completeUpload(uploadId, key, [], bookingId, bookingDetails?.veroaBookingId, plan);

                const completeData = completeRes.data?.success !== undefined ? completeRes.data : { success: true, ...completeRes.data };

                if (completeData.success) {
                    setFiles(prev => prev.map(f => f.id === fileId ? {
                        ...f,
                        status: 'Uploaded successfully',
                        uploaded: true,
                        progress: 100,
                        fileKey: completeData.key || completeData.data?.key || key
                    } : f));
                    toast.success(`${file.name} uploaded!`);
                } else {
                    throw new Error(completeData.message || "Failed to complete upload");
                }

            } catch (error) {
                console.error(`Upload failed for ${file.name}:`, error);
                setFiles(prev => prev.map(f => f.id === fileId ? {
                    ...f,
                    status: 'Upload failed. Remove this file and upload again.',
                    error: true
                } : f));
                toast.error(`Failed to upload ${file.name}. Remove it and try uploading again.`);

                // Try to abort if we have uploadId
                // if (uploadId) abortUpload(uploadId, key).catch(console.error);
            }
            completedFiles++;
            if (completedFiles === totalFiles) {

            }
        }
    };

    const removeFile = async (id) => {
        const fileToRemove = files.find(f => f.id === id);
        
        if (fileToRemove && fileToRemove.uploaded && fileToRemove.fileKey && bookingId) {
            try {
                // If it's an already uploaded file, delete it from S3/DB
                await deleteMedia(bookingId, fileToRemove.fileKey);
            } catch (error) {
                console.error("Error deleting media:", error);
                toast.error("Failed to delete file from server.");
                return; // Stop if server deletion fails? Or proceed? 
                // Usually better to proceed with local removal so UI is responsive, 
                // but here user said it reappears, so we must succeed.
            }
        }

        setFiles(prev => {
            if (fileToRemove?.thumbnail && fileToRemove.thumbnail.startsWith('blob:')) {
                URL.revokeObjectURL(fileToRemove.thumbnail);
            }
            return prev.filter(f => f.id !== id);
        });
        toast.info("File removed");
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const handleSubmit = async () => {
        if (files.length === 0) {
            toast.error("Please upload at least one photo or video");
            return;
        }

        if (files.some(isUploadFailed)) {
            toast.warning("Please remove the failed file(s), re-upload them, and try submitting again.");
            return;
        }

        if (files.some(f => !f.uploaded)) {
            toast.warning("Please wait for all files to finish uploading.");
            return;
        }

        try {
            setSubmitting(true);
            const uploadedFileKeys = files.filter(f => f.uploaded).map(f => f.fileKey);

            if (bookingId) {
                // If we have a bookingId, update the booking with media
                const response = await updateBookingMedia(bookingId, uploadedFileKeys);
                if (response?.data?.success) {
                    toast.success("Media uploaded and linked to booking successfully!");
                    navigate('/booking-success');
                } else {
                    toast.error(response?.data?.message || "Failed to update booking with media");
                }
            } else {
                // Otherwise, use the original add to cart flow
                if (!selectedPlan) {
                    toast.error("Please select a plan first");
                    setSubmitting(false);
                    return;
                }
                const response = await addToCart(selectedPlan._id, 1, uploadedFileKeys);
                if (response?.data?.success) {
                    toast.success("Added to cart successfully!");
                    navigate('/view-cart');
                } else {
                    toast.error(response?.data?.message || "Failed to add to cart");
                }
            }
        } catch (error) {
            console.error("Error submitting:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <button 
                onClick={() => navigate(-1)} 
                style={{ 
                    position: 'absolute', 
                    left: '40px', 
                    top: '120px', 
                    background: 'none', 
                    border: 'none', 
                    cursor: 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    fontSize: '1.1rem', 
                    color: '#332D2D', 
                    fontWeight: '600',
                    padding: '0',
                    zIndex: 10
                }}
            >
                <FaArrowLeft /> Back
            </button>
            <div className={`upload-media-container ${plan}-mode`}>
            
            {/* Plan Toggle */}
            {!isQuoteEditingBooking && (
                <div className="plan-toggle">
                    <div
                        className={`toggle-btn standard ${plan === 'standard' ? 'active' : ''} ${((bookingId && standardEditingVideos === 0) || (cartId && plan !== 'standard')) ? 'disabled' : ''}`}
                        onClick={() => {
                            if ((bookingId && standardEditingVideos === 0) || cartId) return;
                            handlePlanChange('standard');
                        }}
                        style={((bookingId && standardEditingVideos === 0) || (cartId && plan !== 'standard')) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                        Standard
                    </div>
                    <div
                        className={`toggle-btn premium ${plan === 'premium' ? 'active' : ''} ${((bookingId && premiumEditingVideos === 0) || (cartId && plan !== 'premium')) ? 'disabled' : ''}`}
                        onClick={() => {
                            if ((bookingId && premiumEditingVideos === 0) || cartId) return;
                            handlePlanChange('premium');
                        }}
                        style={((bookingId && premiumEditingVideos === 0) || (cartId && plan !== 'premium')) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                        Premium
                    </div>
                </div>
            )}

            <h1 className="main-page-title">
                {bookingId ? (isQuoteEditingBooking ? 'Add Media to Your Booking' : 'Add Media to Your Booking') : (plan === 'premium' ? 'Select Your Premium Plan' : 'Select Your Plan')}
            </h1>





            {(bookingId || cartId) ? (
                !isQuoteEditingBooking && (
                    <div className="total-video-info">
                        {(() => {
                            const total = bookingId ? (plan === 'premium' ? premiumEditingVideos : standardEditingVideos) : (selectedPlan?.numberOfVideos || 0);
                            const currentFilesOfPlan = bookingId ? files.filter(f => f.category === plan) : files;
                            const remaining = Math.max(0, total - currentFilesOfPlan.length);
                            return (
                                <>
                                    <h3>Total videos allowed: <span>{total}</span></h3>
                                    <p style={{ marginTop: '5px', color: '#666', fontSize: '0.9rem' }}>
                                        Remaining videos to upload: <span style={{ fontWeight: '700', color: remaining > 0 ? '#4A3326' : '#4CAF50' }}>{remaining}</span>
                                    </p>
                                </>
                            );
                        })()}
                    </div>
                )
            ) : (
                <>
                    {/* Quantity Dropdown */}
                    <div className="dropdown-container">
                        <div className="custom-dropdown" onClick={() => setShowDropdown(!showDropdown)}>
                            <span>{selectedPlan ? `${selectedPlan.numberOfVideos} Videos` : 'Select Video Quantity'}</span>
                            <IoChevronDownOutline />
                        </div>
                        {showDropdown && (
                            <div className="dropdown-menu-custom">
                                {loading ? (
                                    <div className="dropdown-item-custom">Loading plans...</div>
                                ) : allPlans.length > 0 ? (
                                    allPlans.map((p) => (
                                        <div key={p._id} className="dropdown-item-custom" onClick={() => handleQuantitySelect(p)}>
                                            {p.numberOfVideos} Videos - ₹{p.price}
                                        </div>
                                    ))
                                ) : (
                                    <div className="dropdown-item-custom">No plans available</div>
                                )}
                            </div>
                        )}
                    </div>
                </>
            )}



            {/* Upload Section */}
            <div className="upload-section">
                <h2>{isQuoteEditingBooking ? 'Upload Your Media' : `Upload Your ${plan === 'premium' ? 'High-Res ' : ''}Videos`}</h2>
                <div
                    className="upload-box"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        const droppedFiles = { target: { files: e.dataTransfer.files } };
                        handleFileUpload(droppedFiles);
                    }}
                >
                    <div className="upload-icon-wrapper">
                        <IoCloudUploadOutline />
                    </div>
                    <p className="upload-text">Drag and drop {isQuoteEditingBooking ? 'media' : 'videos'} here <br /> or</p>
                    <input
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept={isQuoteEditingBooking ? "video/*, image/jpeg, .jpg, .jpeg" : "video/*"}
                    />
                    <button className="choose-files-btn" onClick={triggerFileInput}>Choose Files</button>
                </div>
            </div>

            {/* Gallery Grid */}
            <div className="gallery-grid">
                {files.filter(file => !bookingId || file.category === plan).map(file => (
                    <div key={file.id} className="gallery-item">
                        <button className="remove-file-btn" onClick={() => removeFile(file.id)}>
                            <IoCloseOutline />
                        </button>
                        <div className="thumbnail-wrapper" style={{ position: 'relative' }}>
                            {file.type?.startsWith('image/') ? (
                                <img src={file.thumbnail} alt={file.name} className="preview-media" crossOrigin="anonymous" />
                            ) : (
                                <>
                                    <video src={file.thumbnail} className="preview-media" controls crossOrigin="anonymous" />
                                </>
                            )}
                            
                            {!file.uploaded && !file.error && (
                                <div style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: 'white',
                                    borderRadius: '8px',
                                    zIndex: 10
                                }}>
                                    <div className="spinner-border text-light" role="status" style={{ width: '2rem', height: '2rem', marginBottom: '8px' }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <div style={{ fontWeight: 'bold' }}>{file.progress || 0}%</div>
                                </div>
                            )}
                        </div>
                        <div className="file-info">
                            <div className="file-name">{file.name}</div>
                            <div className={`file-status ${getFileStatusClass(file)}`}>
                                {file.uploaded && <AiOutlineCheckCircle />}
                                {file.status}
                                {!file.uploaded && !isUploadFailed(file) && ` (${file.progress || 0}%)`}
                            </div>
                        </div>
                    </div>
                ))}

                {/* Add Placeholder */}
                {(() => {
                    const currentFilesCount = bookingId ? files.filter(f => f.category === plan).length : files.length;
                    const maxFiles = bookingId ? (plan === 'premium' ? premiumEditingVideos : standardEditingVideos) : (selectedPlan?.numberOfVideos || 0);
                    const canAdd = isQuoteEditingBooking ? true : (bookingId ? (currentFilesCount < maxFiles) : (!selectedPlan || currentFilesCount < selectedPlan.numberOfVideos));

                    if (canAdd) {
                        return (
                            <div className="gallery-item add-placeholder" onClick={triggerFileInput}>
                                <IoAddOutline />
                                <div className="placeholder-text">
                                    Add more files <br /> (File {currentFilesCount + 1})
                                </div>
                            </div>
                        );
                    } else {
                        return (
                            <div className="gallery-item add-placeholder disabled">
                                <AiOutlineCheckCircle style={{ color: '#4CAF50' }} />
                                <div className="placeholder-text">
                                    Limit Reached <br /> ({maxFiles} Files)
                                </div>
                            </div>
                        );
                    }
                })()}
            </div>


            {/* Submit Button */}
            <div className="submit-section">
                <button
                    className={`submit-btn ${plan}-submit ${submitting ? 'submitting' : ''}`}
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? 'Processing...' : (bookingId ? 'Submit Media' : (plan === 'premium' ? 'Submit for Premium Editing' : 'Submit for Editing'))}
                </button>
            </div>
        </div>
        </>
    );
};

export default UploadMediaPage;
